import React from 'react';
import { Helmet } from 'react-helmet';
import { toAbsoluteCanonical } from '../../config/knownRoutes';

export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  structuredData?: object;
  noIndex?: boolean;
  noFollow?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

interface SEOHeadProps {
  data: SEOData;
}

const SITE_NAME = 'Brown Tech Int.';

/**
 * Renders head tags through react-helmet so react-snap writes them into the
 * saved HTML. The previous useEffect path skipped the first load, which is
 * the only load the crawler sees.
 * Canonical is omitted when canonical is undefined (the 404 document).
 * A missing canonical must never fall back to the homepage.
 */
const SEOHead: React.FC<SEOHeadProps> = ({ data }) => {
  const {
    title,
    description,
    canonical,
    ogImage,
    ogType = 'website',
    twitterCard = 'summary_large_image',
    noIndex = false,
    noFollow = false,
  } = data;

  const href =
    canonical == null || canonical === ''
      ? null
      : canonical.startsWith('http://') || canonical.startsWith('https://')
        ? canonical
        : toAbsoluteCanonical(canonical);

  const imagePath = ogImage || '/logo512.png';
  const image = imagePath.startsWith('http') ? imagePath : `https://browntech.co${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
  const robots = `${noIndex ? 'noindex' : 'index'}, ${noFollow ? 'nofollow' : 'follow'}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {href && <link rel="canonical" href={href} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {href && <meta property="og:url" content={href} />}
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SEOHead;
